import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Users,
  Target,
  FileText,
  DollarSign,
  Activity,
  ClipboardList,
  Truck,
  BadgeCheck,
  RotateCcw,
  Plus,
  Pencil,
  Trash2,
} from 'lucide-react';
import { Heading } from '../layout';
import { SettingsSplit, TabNavigation } from '../reusable';
import { FormDrawer } from '../forms';
import { ColorPaletteField } from '../forms/ColorPaletteField';
import { useUIColors } from '../../hooks/useTheme';
import { logisticsProvidersAPI } from '../../services/logisticsProvidersAPI';
import {
  CATALOG_DEFINITIONS,
  CATALOG_GROUPS,
  DEFAULT_CATALOG_ITEMS,
  codeFromLabel,
  loadCatalogItemsFromStorage,
  saveCatalogItemsToStorage,
} from './commercialCatalogDefaults';
import { useBusinessUnit } from '../../contexts/BusinessUnitContext';

/** Proveedor del backend -> fila del catálogo de Ajustes. */
const mapProviderToRow = (provider) => ({
  id: String(provider.code),
  code: String(provider.code),
  label: provider.name || '',
  description: provider.description || '',
  sort_order: Number(provider.sort_order || 0),
  is_active: provider.is_active === false ? 'No' : 'Sí',
});

const GROUP_ICONS = {
  leads: Users,
  prospecting: Target,
  tenders: FileText,
  quotes: DollarSign,
  sales: Activity,
  requests: ClipboardList,
  logistics: Truck,
  after_sales: BadgeCheck,
};

const EMPTY_FORM = {
  code: '',
  label: '',
  description: '',
  sort_order: 10,
  color_hex: '#2563eb',
  is_active: 'Sí',
  parent_code: '',
};

const MinimalCatalogTable = ({
  rows = [],
  hasParent = false,
  showColor = true,
  emptyLabel = 'Sin parámetros en este catálogo',
  onAdd,
  onEdit,
  onDelete,
}) => {
  const ui = useUIColors();
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  useEffect(() => {
    setSelectedIds(new Set());
  }, [rows]);

  const sortedRows = useMemo(
    () =>
      [...rows].sort(
        (a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0) || String(a.label).localeCompare(String(b.label), 'es')
      ),
    [rows]
  );

  const allSelected = sortedRows.length > 0 && selectedIds.size === sortedRows.length;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(sortedRows.map((row) => row.id)));
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = () => {
    if (!selectedIds.size) return;
    const selected = sortedRows.filter((row) => selectedIds.has(row.id));
    onDelete?.(selected);
  };

  const thStyle = {
    textAlign: 'left',
    padding: '8px 10px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: ui.muted,
    borderBottom: `1px solid ${ui.border}`,
    whiteSpace: 'nowrap',
  };

  const tdStyle = {
    padding: '9px 10px',
    fontSize: '13px',
    color: ui.text,
    borderBottom: `1px solid color-mix(in srgb, ${ui.border} 70%, transparent)`,
    verticalAlign: 'middle',
  };

  const iconBtn = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: 6,
    border: 'none',
    background: 'transparent',
    color: ui.muted,
    cursor: 'pointer',
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '8px',
          marginBottom: '8px',
        }}
      >
        {selectedIds.size > 0 ? (
          <button
            type="button"
            onClick={handleBulkDelete}
            style={{
              ...iconBtn,
              width: 'auto',
              padding: '0 10px',
              gap: 6,
              color: '#b91c1c',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            <Trash2 size={14} />
            Eliminar ({selectedIds.size})
          </button>
        ) : null}
        <button
          type="button"
          onClick={onAdd}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            height: 30,
            padding: '0 10px',
            borderRadius: 6,
            border: `1px solid ${ui.border}`,
            background: 'transparent',
            color: ui.text,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <Plus size={14} />
          Agregar
        </button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: hasParent ? 720 : 640 }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: 36 }}>
                <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Seleccionar todos" />
              </th>
              <th style={thStyle}>Código</th>
              <th style={thStyle}>Etiqueta</th>
              {hasParent ? <th style={thStyle}>Industria padre</th> : null}
              <th style={thStyle}>Descripción</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Orden</th>
              {showColor ? <th style={thStyle}>Color</th> : null}
              <th style={thStyle}>Activo</th>
              <th style={{ ...thStyle, textAlign: 'right' }} />
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 ? (
              <tr>
                <td
                  colSpan={(hasParent ? 9 : 8) - (showColor ? 0 : 1)}
                  style={{ ...tdStyle, textAlign: 'center', color: ui.muted, padding: '28px 10px' }}
                >
                  {emptyLabel}
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => (
                <tr key={row.id}>
                  <td style={tdStyle}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row.id)}
                      onChange={() => toggleOne(row.id)}
                      aria-label={`Seleccionar ${row.label}`}
                    />
                  </td>
                  <td style={{ ...tdStyle, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}>
                    {row.code}
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{row.label}</td>
                  {hasParent ? <td style={tdStyle}>{row.parent_label || '—'}</td> : null}
                  <td style={{ ...tdStyle, color: ui.muted, maxWidth: 280 }}>
                    <span
                      style={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={row.description || ''}
                    >
                      {row.description || '—'}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {row.sort_order ?? '—'}
                  </td>
                  {showColor ? (
                    <td style={tdStyle}>
                      <span
                        style={{
                          display: 'inline-block',
                          width: 14,
                          height: 14,
                          borderRadius: 4,
                          background: row.color_hex || ui.border,
                          border: `1px solid ${ui.border}`,
                        }}
                      />
                    </td>
                  ) : null}
                  <td style={tdStyle}>
                    <span style={{ color: String(row.is_active).toLowerCase() === 'sí' ? ui.text : ui.muted }}>
                      {row.is_active || '—'}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button type="button" title="Editar" onClick={() => onEdit?.(row)} style={iconBtn}>
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      title="Eliminar"
                      onClick={() => onDelete?.([row])}
                      style={{ ...iconBtn, color: '#b91c1c' }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const GroupCatalogPanel = ({
  groupKey,
  selectedCatalogByGroup,
  setSelectedCatalogByGroup,
  catalogItemsByKey,
  apiRowsByKey = {},
  apiErrorsByKey = {},
  openAdd,
  openEdit,
  handleDelete,
}) => {
  const ui = useUIColors();
  const definitions = CATALOG_DEFINITIONS.filter((item) => item.group === groupKey);
  const selectedCatalogKey = selectedCatalogByGroup[groupKey] || definitions[0]?.key;
  const selectedDefinition = CATALOG_DEFINITIONS.find((item) => item.key === selectedCatalogKey);
  const isApiBacked = selectedDefinition?.storage === 'api';
  const rows = isApiBacked
    ? apiRowsByKey[selectedCatalogKey] || []
    : catalogItemsByKey[selectedCatalogKey] || [];
  const apiError = isApiBacked ? apiErrorsByKey[selectedCatalogKey] || '' : '';

  if (!definitions.length) {
    return (
      <div
        style={{
          padding: '28px 16px',
          border: `1px dashed ${ui.border}`,
          borderRadius: 10,
          color: ui.muted,
          fontSize: 13,
          textAlign: 'center',
        }}
      >
        Aún no hay parámetros ajustables en esta sección.
      </div>
    );
  }

  return (
    <SettingsSplit
      ariaLabel="Catálogos"
      items={definitions.map((definition) => ({ key: definition.key, label: definition.label }))}
      activeKey={selectedCatalogKey}
      onChange={(key) =>
        setSelectedCatalogByGroup((prev) => ({
          ...prev,
          [groupKey]: key,
        }))
      }
    >
        <div style={{ marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: ui.text }}>
            {selectedDefinition?.label || 'Catálogo'}
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted, lineHeight: 1.4 }}>
            {selectedDefinition?.description || ''}
          </p>
        </div>

        {apiError ? (
          <div
            style={{
              marginBottom: 10,
              padding: '8px 10px',
              borderRadius: 8,
              border: '1px solid #fca5a5',
              background: 'color-mix(in srgb, #fee2e2 40%, transparent)',
              color: '#b91c1c',
              fontSize: 12,
            }}
          >
            {apiError}
          </div>
        ) : null}

        <MinimalCatalogTable
          rows={rows}
          hasParent={Boolean(selectedDefinition?.hasParent)}
          showColor={!isApiBacked}
          emptyLabel={
            isApiBacked ? 'Sin proveedores registrados' : 'Sin parámetros en este catálogo'
          }
          onAdd={() => openAdd(selectedCatalogKey)}
          onEdit={(row) => openEdit(selectedCatalogKey, row)}
          onDelete={(payload) => handleDelete(selectedCatalogKey, payload)}
        />
    </SettingsSplit>
  );
};

const PaintCommercialConfigWorkspace = ({
  isDarkMode = false,
  groupKeys = null,
  title = 'Ajustes Comercial',
  subtitle = 'Define los valores de las listas usadas en leads, cotizaciones, ventas, solicitudes T-COAT y logística. Los catálogos de Solicitudes se reflejan en Planificación y Producción.',
}) => {
  const ui = useUIColors();
  const unit = useBusinessUnit();
  void isDarkMode;
  const visibleGroups = CATALOG_GROUPS.filter((group) =>
    Array.isArray(groupKeys) && groupKeys.length ? groupKeys.includes(group.key) : true
  );

  const [catalogItemsByKey, setCatalogItemsByKey] = useState(
    () => loadCatalogItemsFromStorage(unit) || DEFAULT_CATALOG_ITEMS
  );
  const [selectedCatalogByGroup, setSelectedCatalogByGroup] = useState({
    leads: 'segment',
    prospecting: 'funnel_stage',
    tenders: 'tender_status',
    quotes: 'quotation_status',
    sales: 'sale_status',
    requests: 'product_request_status',
    logistics: 'dispatch_status',
    after_sales: '',
  });
  const [showForm, setShowForm] = useState(false);
  const [editingCatalogKey, setEditingCatalogKey] = useState('');
  const [editingItemId, setEditingItemId] = useState(null);
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [providerRows, setProviderRows] = useState([]);
  const [providersError, setProvidersError] = useState('');

  useEffect(() => {
    saveCatalogItemsToStorage(catalogItemsByKey, unit);
  }, [catalogItemsByKey, unit]);

  const refreshProviders = useCallback(async () => {
    try {
      const rows = await logisticsProvidersAPI.getAll({ includeInactive: true });
      setProviderRows((Array.isArray(rows) ? rows : []).map(mapProviderToRow));
      setProvidersError('');
    } catch (error) {
      setProvidersError(error?.message || 'No se pudieron cargar los proveedores logísticos.');
    }
  }, []);

  useEffect(() => {
    refreshProviders();
  }, [refreshProviders]);

  const editingDefinition = CATALOG_DEFINITIONS.find((item) => item.key === editingCatalogKey);
  const parentOptions = useMemo(() => {
    if (!editingDefinition?.hasParent || !editingDefinition.parentCatalogKey) return [];
    return (catalogItemsByKey[editingDefinition.parentCatalogKey] || [])
      .filter((row) => String(row.is_active).toLowerCase() === 'sí')
      .map((row) => ({ value: row.code, label: row.label }));
  }, [editingDefinition, catalogItemsByKey]);

  const openAdd = async (catalogKey) => {
    const definition = CATALOG_DEFINITIONS.find((item) => item.key === catalogKey);
    const isApiBacked = definition?.storage === 'api';
    const current = isApiBacked ? providerRows : catalogItemsByKey[catalogKey] || [];
    const nextOrder =
      current.length > 0 ? Math.max(...current.map((row) => Number(row.sort_order || 0))) + 10 : 10;
    setEditingCatalogKey(catalogKey);
    setEditingItemId(null);
    setFormError('');
    setFormData({ ...EMPTY_FORM, sort_order: nextOrder });
    setShowForm(true);
    if (!isApiBacked) return;
    try {
      const { code } = await logisticsProvidersAPI.getNextCode();
      setFormData((prev) => ({ ...prev, code: code || '' }));
    } catch {
      // Sin código sugerido el backend lo asigna igual al guardar.
    }
  };

  const openEdit = (catalogKey, rowToEdit) => {
    if (!rowToEdit) return;
    setEditingCatalogKey(catalogKey);
    setEditingItemId(rowToEdit.id);
    setFormError('');
    setFormData({
      code: rowToEdit.code || '',
      label: rowToEdit.label || '',
      description: rowToEdit.description || '',
      sort_order: Number(rowToEdit.sort_order || 10),
      color_hex: rowToEdit.color_hex || '#2563eb',
      is_active: rowToEdit.is_active || 'Sí',
      parent_code: rowToEdit.parent_code || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (catalogKey, payload) => {
    const rowsToDelete = Array.isArray(payload) ? payload : [payload];
    const idsToDelete = new Set(rowsToDelete.map((row) => row?.id).filter(Boolean));
    if (!idsToDelete.size) return;

    const definition = CATALOG_DEFINITIONS.find((item) => item.key === catalogKey);
    if (definition?.storage === 'api') {
      const ok = window.confirm(
        'Se desactivará el proveedor. Los viajes ya emitidos conservan su código y nombre. ¿Continuar?'
      );
      if (!ok) return;
      try {
        await Promise.all([...idsToDelete].map((code) => logisticsProvidersAPI.remove(code)));
        await refreshProviders();
      } catch (error) {
        setProvidersError(error?.message || 'No se pudo eliminar el proveedor.');
      }
      return;
    }

    setCatalogItemsByKey((prev) => ({
      ...prev,
      [catalogKey]: (prev[catalogKey] || []).filter((row) => !idsToDelete.has(row.id)),
    }));
  };

  const handleResetDefaults = () => {
    const ok = window.confirm(
      '¿Restablecer todos los catálogos a los valores por defecto de producción? Se perderán cambios locales.'
    );
    if (!ok) return;
    setCatalogItemsByKey(DEFAULT_CATALOG_ITEMS);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingItemId(null);
    setEditingCatalogKey('');
    setFormData({ ...EMPTY_FORM });
  };

  const handleSave = async () => {
    const label = String(formData.label || '').trim();
    let code = String(formData.code || '').trim();
    if (!label) {
      setFormError('La etiqueta es obligatoria.');
      return;
    }

    const catalogKey = editingCatalogKey;
    if (!catalogKey) return;
    const definition = CATALOG_DEFINITIONS.find((item) => item.key === catalogKey);

    if (definition?.storage === 'api') {
      const body = {
        code,
        name: label,
        description: String(formData.description || '').trim(),
        sort_order: Number(formData.sort_order || 0),
        is_active: String(formData.is_active || 'Sí').toLowerCase() === 'sí',
      };
      setIsSaving(true);
      try {
        if (editingItemId) await logisticsProvidersAPI.update(editingItemId, body);
        else await logisticsProvidersAPI.create(body);
        await refreshProviders();
        closeForm();
      } catch (error) {
        setFormError(error?.message || 'No se pudo guardar el proveedor.');
      } finally {
        setIsSaving(false);
      }
      return;
    }

    if (!code) code = codeFromLabel(label);
    if (!code) {
      setFormError('Código y etiqueta son obligatorios.');
      return;
    }
    if (definition?.hasParent && !String(formData.parent_code || '').trim()) {
      setFormError('Debes indicar la industria padre del rubro.');
      return;
    }

    const parentRow = (catalogItemsByKey.industry || []).find(
      (row) => row.code === formData.parent_code
    );

    setCatalogItemsByKey((prev) => {
      const current = prev[catalogKey] || [];
      const nextRow = {
        code,
        label,
        description: String(formData.description || '').trim(),
        sort_order: Number(formData.sort_order || 0),
        color_hex: formData.color_hex || '#2563eb',
        is_active: formData.is_active || 'Sí',
        parent_code: definition?.hasParent ? formData.parent_code : '',
        parent_label: definition?.hasParent ? parentRow?.label || '' : '',
      };

      if (editingItemId) {
        return {
          ...prev,
          [catalogKey]: current.map((row) =>
            row.id === editingItemId ? { ...row, ...nextRow } : row
          ),
        };
      }
      const nextId = current.length ? Math.max(...current.map((row) => Number(row.id || 0))) + 1 : 1;
      return {
        ...prev,
        [catalogKey]: [...current, { id: nextId, ...nextRow }],
      };
    });

    closeForm();
  };

  const isApiDefinition = editingDefinition?.storage === 'api';

  const formSections = [
    {
      key: 'basic',
      title: isApiDefinition ? 'Proveedor logístico' : 'Parámetro',
      fields: [
        {
          key: 'code',
          label: isApiDefinition ? 'Código (correlativo del viaje)' : 'Código',
          // El código forma el correlativo TRPYY-ZZXXX, así que se fija al crear.
          disabled: isApiDefinition,
          readOnly: isApiDefinition,
        },
        { key: 'label', label: isApiDefinition ? 'Nombre' : 'Etiqueta' },
        { key: 'description', label: 'Descripción', fullWidth: true },
        ...(editingDefinition?.hasParent
          ? [
              {
                key: 'parent_code',
                label: 'Industria padre',
                type: 'select',
                options: [
                  { value: '', label: 'Seleccionar industria' },
                  ...parentOptions,
                ],
              },
            ]
          : []),
        { key: 'sort_order', label: 'Orden', type: 'number' },
        ...(isApiDefinition
          ? []
          : [
              {
                key: 'color_hex',
                label: 'Color',
                type: 'custom',
                fullWidth: true,
                render: ({ value, onChange, colors }) => (
                  <ColorPaletteField
                    value={value}
                    onChange={onChange}
                    label="Color"
                    muted={colors.muted}
                    text={colors.text}
                    border={colors.inputBorder || colors.sectionBorder}
                  />
                ),
              },
            ]),
        {
          key: 'is_active',
          label: 'Activo',
          type: 'select',
          options: [
            { value: 'Sí', label: 'Sí' },
            { value: 'No', label: 'No' },
          ],
        },
      ],
    },
  ];

  const tabs = visibleGroups.map((group) => {
    const Icon = GROUP_ICONS[group.key] || Users;
    return {
      key: group.key,
      label: group.label,
      icon: <Icon size={16} />,
      content: (
        <GroupCatalogPanel
          groupKey={group.key}
          selectedCatalogByGroup={selectedCatalogByGroup}
          setSelectedCatalogByGroup={setSelectedCatalogByGroup}
          catalogItemsByKey={catalogItemsByKey}
          apiRowsByKey={{ logistics_provider: providerRows }}
          apiErrorsByKey={{ logistics_provider: providersError }}
          openAdd={openAdd}
          openEdit={openEdit}
          handleDelete={handleDelete}
        />
      ),
    };
  });

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap',
          marginBottom: '16px',
        }}
      >
        <div>
          <Heading level={2} isDarkMode={isDarkMode} style={{ marginBottom: '6px' }}>
            {title}
          </Heading>
          <p style={{ margin: 0, maxWidth: 640, fontSize: '13px', color: ui.muted, lineHeight: 1.45 }}>
            {subtitle}
          </p>
        </div>
        <button
          type="button"
          onClick={handleResetDefaults}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            height: '30px',
            padding: '0 10px',
            borderRadius: '6px',
            border: 'none',
            background: 'transparent',
            color: ui.muted,
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <RotateCcw size={14} />
          Restablecer defaults
        </button>
      </div>

      <TabNavigation tabs={tabs} defaultTab={visibleGroups[0]?.key || 'leads'} isDarkMode={isDarkMode} />

      <FormDrawer
        isOpen={showForm}
        onClose={closeForm}
        onSave={handleSave}
        isDarkMode={isDarkMode}
        title={
          isApiDefinition
            ? editingItemId
              ? 'Editar proveedor'
              : 'Nuevo proveedor'
            : editingItemId
              ? 'Editar parámetro'
              : 'Nuevo parámetro'
        }
        subtitle={editingDefinition?.label || 'Catálogo'}
        sections={formSections}
        formData={formData}
        onChange={(key, value) => {
          setFormData((prev) => {
            const next = { ...prev, [key]: value };
            // El código de proveedor lo asigna el backend; no derivarlo del nombre.
            if (
              key === 'label' &&
              !isApiDefinition &&
              !editingItemId &&
              !String(prev.code || '').trim()
            ) {
              next.code = codeFromLabel(value);
            }
            return next;
          });
        }}
        formError={formError}
        isSaving={isSaving}
        width={540}
      />
    </div>
  );
};

export default PaintCommercialConfigWorkspace;
